import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faCheck, faTimes, faCopy } from '@fortawesome/free-solid-svg-icons';
import { customStyles } from '../../tableStyles';
import axios from 'axios';
import '../css/PersonList.css'
import '../css/EmailCopyControl.css'
import { dedupePersonRoleDisplay } from '../../utils/dedupePersonRoles';
import { Pointer } from 'lucide-react';


function   PersonList() {


  const [persons, setPersons] = useState([]);
  const [filteredPersons, setFilteredPersons] = useState([]);
  const [organisations, setOrganisations] = useState({});
  const [roles, setRoles] = useState({});
  const [personRoles, setPersonRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);


  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;

  // Fetch reference lookup tables (orgs, roles, personRoles) once on mount.
  // These are small and used for the Organisations/Roles columns.
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [orgRes, roleRes, personRoleRes] = await Promise.all([
          axios.get(`${API}/organisations`),
          axios.get(`${API}/roles`),
          axios.get(`${API}/personRoles`),
        ]);

        const orgMap = orgRes.data.reduce((map, org) => {
          map[org._id] = org.name;
          return map;
        }, {});

        const roleMap = roleRes.data.reduce((map, role) => {
          map[role._id] = role.role_name;
          return map;
        }, {});

        setOrganisations(orgMap);
        setRoles(roleMap);
        setPersonRoles(personRoleRes.data);
      } catch (error) {
        console.error('Error fetching lookup data:', error);
      }
    };

    fetchLookups();
  }, [API]);

  // Debounce search input by 300ms to avoid firing request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset to page 1 whenever search term changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Fetch paginated persons whenever page, rowsPerPage, or search changes.
  const fetchPersons = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/persons`, {
        params: {
          page,
          limit: rowsPerPage,
          search: debouncedSearch || undefined,
        },
      });
      // Paginated response shape: { data, total, page, limit }
      setPersons(res.data.data);
      setFilteredPersons(res.data.data);
      setTotalRows(res.data.total);
    } catch (error) {
      console.error('Error fetching persons:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPersons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setToastMessage("Email copied to clipboard!");
    setShowToast(false);
    setTimeout(() => { setShowToast(true); }, 10);
    setTimeout(() => { setShowToast(false); }, 3000);
  };

  // Toggle Xmas Card (xmas1)
const toggleXmas = async (personId) => {
  try {
    const res = await axios.put(`${process.env.REACT_APP_API_URL}/persons/xmas/${personId}`);
    setPersons((prev) =>
      prev.map((p) => (p._id === personId ? { ...p, xmas1: res.data.xmas1 } : p))
    );
    setFilteredPersons((prev) =>
      prev.map((p) => (p._id === personId ? { ...p, xmas1: res.data.xmas1 } : p))
    );
    setToastMessage(res.data.message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  } catch (err) {
    console.error("Error toggling Xmas card:", err);
  }
};

// Toggle CEO Xmas Card (xmas2)
const toggleCeoXmas = async (personId) => {
  try {
    const res = await axios.put(`${process.env.REACT_APP_API_URL}/persons/ceoxmas/${personId}`);
    setPersons((prev) =>
      prev.map((p) => (p._id === personId ? { ...p, xmas2: res.data.xmas2 } : p))
    );
    setFilteredPersons((prev) =>
      prev.map((p) => (p._id === personId ? { ...p, xmas2: res.data.xmas2 } : p))
    );
    setToastMessage(res.data.message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  } catch (err) {
    console.error("Error toggling CEO Xmas card:", err);
  }
};

  // Fetch xmas-flagged emails from backend — spans entire DB, not just current page.
  const copyXmasEmailsByFlag = async (flag) => {
    try {
      const res = await axios.get(`${API}/persons/xmas-emails`, { params: { flag } });
      const emails = res.data || [];
      if (emails.length === 0) {
        setToastMessage("No emails to copy.");
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
        return;
      }
      await navigator.clipboard.writeText(emails.join(", "));
      setToastMessage("Emails copied to clipboard!");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    } catch (err) {
      console.error("Error copying xmas emails:", err);
    }
  };

  const copyXmasEmails    = () => copyXmasEmailsByFlag("xmas1");
  const copyCeoXmasEmails = () => copyXmasEmailsByFlag("xmas2");

  const getRelationships = (personId) => {
    const relations = personRoles.filter(
      (role) => String(role.person_id) === String(personId)
    );
    return dedupePersonRoleDisplay(relations, organisations, roles);
  };

  // Search is handled server-side via debouncedSearch — no client-side filter needed.
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRowClick = (row) => {
    navigate(`/edit-person/${row._id}`, { state: { person: row } }); // Use _id
  };

  const handleDelete = async (_id) => {
    try {
      await axios.delete(`${API}/persons/${_id}`);
      // Refetch current page so totalRows stays accurate.
      // If this was last row on last page, step back one page.
      const isLastRowOnPage = persons.length === 1 && page > 1;
      if (isLastRowOnPage) {
        setPage(page - 1);
      } else {
        fetchPersons();
      }
    } catch (error) {
      console.error('Error deleting person:', error);
    }
  };

  const openDialog = (person) => {
    setSelectedPerson(person);
    setDialogVisible(true);
  };

  const confirmDelete = () => {
    if (selectedPerson) {
      handleDelete(selectedPerson._id); // Use _id
    }
    setDialogVisible(false);
    setSelectedPerson(null);
  };

  const cancelDelete = () => {
    setDialogVisible(false);
    setSelectedPerson(null);
  };

  const columns = [
    {
      name: 'Image',
      selector: (row) => row.image,
      width : '80px',
      left : true,
      cell: (row) => (
        <div className="person-list-cell-image">
          <img
            src={row.image ? `/images/${row.image}` : '/images/avatar.png'}
            alt={row.name}
            style={{ width: '50px', height: '50px', borderRadius: '4px' }}
          />
        </div>
      ),
      ignoreRowClick: true,
    },
    {
      name: 'Name',
      selector: (row) => `${row.name} ${row.mname || ''} ${row.lname}`,
      sortable: true,
      left: true,
      cell: (row) => (
        <span
          className="person-list-name-link"
          style={{ color: '#000', cursor: 'pointer' }}
          onClick={() => handleRowClick(row)}
        >
          {row.name} {row.mname || ''} {row.lname}
        </span>
      ),
    },
  {
  name: 'Email',
  selector: (row) => row.email,
  sortable: true,
  minWidth: '320px',
  width: '480px',
  left: true,
  cell: (row) => (
    <div className="email-copy-cell">
      <span className="email-text">{row.email}</span>

      <button
        type="button"
        className="email-copy-btn"
        onClick={(e) => {
          e.stopPropagation();
          copyToClipboard(row.email);
        }}
        title="Copy Email"
      >
        <FontAwesomeIcon icon={faCopy} style={{ color: '#D9A128' }} />
      </button>
    </div>
  )
},

    {
      name: 'Organisations',
      left: true,
      grow : 2,
      cell: (row) => {
        const relationships = getRelationships(row._id); // Use _id
        return (
          <ul className="person-list-cell-list">
            {relationships.filter((r) => r.organisation).map((relation, index) => (
              <li key={index}>{relation.organisation}</li>
            ))}
          </ul>
        );
      },
    },
    {
      name: 'Roles',
      left: true,
      grow : 1,
      cell: (row) => {
        const relationships = getRelationships(row._id); // Use _id
        return (
          <ul className="person-list-cell-list">
            {relationships.map((relation, index) => (
              <li key={index}>{relation.role}</li>
            ))}
          </ul>
        );
      },
    },


    {
  name: (
    <div className="person-list-header-with-copy">
      <span>Xmas Cards</span>
      <button
        type="button"
        className="person-list-icon-btn"
        onClick={(e) => {
          e.stopPropagation();
          copyXmasEmails();
        }}
        title="Copy Xmas Emails"
      >
        <FontAwesomeIcon icon={faCopy} style={{ color: '#fff' }} />
      </button>
    </div>
  ),
  width: '155px',
  minWidth: '155px',
  center: true,
  button: true,
  sortable: false,
  ignoreRowClick: true,
  cell: (row) => (
    <div className="person-list-cell-center">
      <button
        type="button"
        className={`person-list-xmas-toggle ${row.xmas1 ? 'person-list-xmas-toggle--on' : 'person-list-xmas-toggle--off'}`}
        onClick={(e) => { e.stopPropagation(); toggleXmas(row._id); }}
        title={row.xmas1 ? 'Remove from Xmas Cards' : 'Add to Xmas Cards'}
      >
        <FontAwesomeIcon icon={row.xmas1 ? faCheck : faTimes} />
      </button>
    </div>
  ),
},
{
  name: (
    <div className="person-list-header-with-copy">
      <span>CEO Xmas Cards</span>
      <button
        type="button"
        className="person-list-icon-btn"
        onClick={(e) => {
          e.stopPropagation();
          copyCeoXmasEmails();
        }}
        title="Copy CEO Xmas Emails"
      >
        <FontAwesomeIcon icon={faCopy} style={{ color: '#fff' }} />
      </button>
    </div>
  ),
  width: '185px',
  minWidth: '185px',
  center: true,
  button: true,
  sortable: false,
  ignoreRowClick: true,
  cell: (row) => (
    <div className="person-list-cell-center">
      <button
        type="button"
        className={`person-list-xmas-toggle ${row.xmas2 ? 'person-list-xmas-toggle--on' : 'person-list-xmas-toggle--off'}`}
        onClick={(e) => { e.stopPropagation(); toggleCeoXmas(row._id); }}
        title={row.xmas2 ? 'Remove from CEO Xmas Cards' : 'Add to CEO Xmas Cards'}
      >
        <FontAwesomeIcon icon={row.xmas2 ? faCheck : faTimes} />
      </button>
    </div>
  ),
},

    {
      name: 'Delete',
      width: '95px',
      minWidth: '95px',
      center: true,
      headerStyle: {
        justifyContent: 'center',
        textAlign: 'center',
      },
      cell: (row) => (
        <div className="person-list-cell-center">
          <button
            type="button"
            className="person-list-icon-btn person-list-delete-btn"
            onClick={() => openDialog(row)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,  
    },
  ];

  return (
    <div style={{ background: "transparent", padding: "0px" }}>
      <div
        className="d-flex justify-content-between mb-1"
        style={{ marginTop: "40px" }}
      >
        <div className="w-25 me-3 mb-1">
          <input
          type="text"
          placeholder="Search by name, email, organisation, or role"
          className="individuals-search-wrapper form-control search-input"
          value={searchTerm}
          onChange={handleSearch}
          title="Search participants"
          style={{ border: "1px solid #000" }}
         />
      </div>

    <div className="show-section">
     <span>Show</span>
     <select
      value={rowsPerPage}
      onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
      className="show-dropdown-select"
    >
      <option value={10}>10</option>
      <option value={25}>25</option>
      <option value={50}>50</option>
      <option value={100}>100</option>
    </select>
  </div>
</div>

          <div className="crm-table-wrapper">
            <DataTable
              key={rowsPerPage}
              columns={columns}
              data={filteredPersons}
              progressPending={loading}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={rowsPerPage}
              paginationDefaultPage={page}
              paginationComponentOptions={{ noRowsPerPage: true }}
              onChangePage={(newPage) => setPage(newPage)}
              highlightOnHover
              striped
              responsive
              customStyles={customStyles}
              onRowClicked={handleRowClick}
            />
          </div>
      {dialogVisible && (
    <div className="dialog-overlay">
      <div className="dialog-box">
        <h4>Confirm Delete</h4>
        <p>Are you sure you want to delete {selectedPerson?.name}?</p>
        <button onClick={confirmDelete} className="dialog-confirm-button">
          Confirm
        </button>
        <button onClick={cancelDelete} className="dialog-cancel-button">
          Cancel
        </button>
      </div>
    </div>
  )}
  {showToast && (
      <div className="copy-toast">
        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '10px', color: '#D9A128' }} />
        {toastMessage}

      </div>
    )}

    </div>

  );
}

export default PersonList;